import { fetcher } from '@/lib/fetcher';
import { ChatGiftItem } from '@/types/chat';
import { UploadUserImageResponse } from '@/types/common';
import { StreamingRankingType } from '@/types/donation';
import {
    CommandType,
    FetchSDPResponse,
    LabelType,
    SettingInfoType,
    StreamingInfoFromApi,
    StartStreamingInfoFromApi,
    StreamingType,
    UpdateCoverType,
    UpdateSettingInfoType,
    GiftCategory,
    CommandRecordType,
    EnterStreamingInfoFromApi,
    Announcement,
    MuteStatusFromApi,
    PaymentInfo
} from '@/types/streaming';
import { getApiPath } from '@/utils/handleENV';

export const getCommendList = () => {
    return fetcher<CommandType[], null>({
        url: '/livestream/v1/command/me',
        method: 'GET'
    });
};

export const addCommand = (data: CommandType) => {
    return fetcher<null, CommandType>({
        url: '/livestream/v1/command',
        method: 'POST',
        data: data
    });
};

export const updateCommand = (data: CommandType) => {
    return fetcher<null, CommandType>({
        url: `/livestream/v1/command/${data.id}`,
        method: 'PUT',
        data
    });
};

export const deleteCommand = (id: string) => {
    return fetcher<null, string>({
        url: `/livestream/v1/command/${id}`,
        method: 'DELETE'
    });
};

export const executeCommand = (liveId: string, commandId: string) => {
    return fetcher<null, null>({
        url: `/livestream/v1/live/${liveId}/command-record/${commandId}/execute`,
        method: 'POST'
    });
};

export const completeCommand = (liveId: string, commandId: string) => {
    return fetcher<null, CommandType>({
        url: `/livestream/v1/live/${liveId}/command-record/${commandId}/complete`,
        method: 'POST'
    });
};

export const getStreamingCommandList = (liveId: string) => {
    return fetcher<CommandType[], null>({
        url: `/livestream/v1/live/${liveId}/command`,
        method: 'GET'
    });
};

export const getStreamingCommandRecordList = (liveId: string) => {
    return fetcher<CommandRecordType[], null>({
        url: `/livestream/v1/live/${liveId}/command-record`,
        method: 'GET'
    });
};

export const buyCommand = (liveId: string, commandId: string) => {
    return fetcher<null, { command_id: string }>({
        url: `/livestream/v1/live/${liveId}/command`,
        method: 'POST',
        data: {
            command_id: commandId
        }
    });
};

export const getBaseSetting = () => {
    return fetcher<SettingInfoType, null>({
        url: '/livestream/v1/setting/me',
        method: 'GET'
    });
};

export const updateBaseSetting = (data: UpdateSettingInfoType) => {
    return fetcher<SettingInfoType, UpdateSettingInfoType>({
        url: '/livestream/v1/setting/me',
        method: 'PUT',
        data
    });
};

export const getLabels = () => {
    return fetcher<LabelType[], null>({
        url: '/livestream/v1/label',
        method: 'GET'
    });
};

// 取得上傳presign url
export const getUploadCoverUrl = () => {
    return fetcher<UploadUserImageResponse, null>({
        url: '/livestream/v1/cover/upload-url',
        method: 'POST'
    });
};

// 實際更新直播封面照片
export const updateUploadCoverUrl = (cover: string) => {
    return fetcher<null, UpdateCoverType>({
        url: '/livestream/v1/cover',
        method: 'PUT',
        data: { cover }
    });
};

export const getStreaming = (liveId: string) => {
    return fetcher<StreamingInfoFromApi, null>({
        url: `/livestream/v1/live/${liveId}`,
        method: 'GET'
    });
};

export const getAllStreaming = async ({ limit, page, xCursor }: { limit: number, page?: number, xCursor: string }) => {
    const apiPath = getApiPath();
    // const p = page === 1 ? `&page=${page}` : "";
    const res = await fetch(`${apiPath}/livestream/v1/all?limit=${limit}&page=${page}`, {
        headers: {
            "x-Cursor": xCursor
        },
        method: "GET"
    });
    const xcursor = res.headers.get('x-Cursor');
    const data = await res.json();

    if (res.status === 200) return { status: res.status, data: data as StreamingInfoFromApi[], xcursor };
    return { status: 400, errorMsg: data.msg }
};

export const getHotStreaming = ({ userId, limit }: { userId: string, limit: number }) => {
    return fetcher<StreamingInfoFromApi[], { user_id: string, limit: number }>({
        url: `/livestream/v1/hot?`,
        method: 'GET',
        data: { user_id: userId, limit }
    });
};

export const getScrollStreaming = async ({ limit, page, xCursor }: { limit: number, page?: number, xCursor: string }) => {
    const p = page === 1 ? `&page=${page}` : "";
    const apiPath = getApiPath();
    const res = await fetch(`${apiPath}/livestream/v1/scroll?limit=${limit}&page=${page}`, {
        headers: {
            "x-Cursor": xCursor,
        },
        method: "GET",
        cache: "no-store",
        next: { revalidate: 0 },
    });
    const xcursor = res.headers.get('x-Cursor');
    const data = await res.json();
    // console.log("limit", limit)
    // console.log("page", page)
    // console.log("xCursor", xCursor)

    if (res.status === 200) return { status: res.status, data: data as StreamingInfoFromApi[], xcursor };
    return { status: 400, errorMsg: data.msg }
};

export const enterStreaming = (liveId: string) => {
    return fetcher<EnterStreamingInfoFromApi, null>({
        url: `/livestream/v1/live/${liveId}/enter`,
        method: 'POST'
    });
};

export const createStreaming = () => {
    return fetcher<StartStreamingInfoFromApi, null>({
        url: '/livestream/v1/live',
        method: 'POST'
    });
};

export const endStreaming = (id: string) => {
    return fetcher<{ total_income: number }, null>({
        url: `/livestream/v1/live/${id}`,
        method: 'DELETE'
    });
};

export const getStreamingByUser = (userId: string) => {
    return fetcher<StreamingInfoFromApi, null>({
        url: `/livestream/v1/user/${userId}`,
        method: 'GET'
    });
};

export const sendSdp = () => {
    return fetcher<FetchSDPResponse, null>({
        url: '/livestream/v1/cover/upload-url',
        method: 'POST'
    });
};

export const changeStreamingType = (id: string, type: StreamingType) => {
    return fetcher<{ push_url: string }, { type: number }>({
        url: `/livestream/v1/live/${id}/type`,
        method: "PATCH",
        data: { type }
    })
}


export const getGiftCategory = (liveId: string) => {
    return fetcher<GiftCategory[], { type: number }>({
        url: `/livestream/v1/live/${liveId}/gift/category`,
        method: "GET"
    })
}

export const getGiftByCategory = (liveId: string, categoryId: string) => {
    return fetcher<ChatGiftItem[], { type: number }>({
        url: `/livestream/v1/live/${liveId}/gift/category/${categoryId}`,
        method: "GET"
    })
}

export const sendGift = (liveId: string, giftId: number) => {
    return fetcher<null, { gift_id: number }>({
        url: `/livestream/v1/live/${liveId}/gift`,
        method: 'POST',
        data: { gift_id: giftId }
    });
};

export const getAnnouncement = (liveId: string) => {
    return fetcher<Announcement, null>({
        url: `/livestream/v1/live/${liveId}/announcement`,
        method: 'GET',
    });
};

export const updateAnnouncement = (liveId: string, announcement: Announcement) => {
    return fetcher<null, Announcement>({
        url: `/livestream/v1/live/${liveId}/announcement`,
        method: 'PUT',
        data: announcement
    });
};

export const getRanking = (liveId: string) => {
    return fetcher<StreamingRankingType[], null>({
        url: `/livestream/v1/live/${liveId}/rank`,
        method: "GET",
        cache: 'no-store'
    })
}

export const payment = (liveId: string) => {
    return fetcher<PaymentInfo, null>({
        url: `/livestream/v1/live/${liveId}/payment`,
        method: "POST"
    })
}

export const getIsUserMuted = (liveId: string, userId: string) => {
    return fetcher<MuteStatusFromApi, null>({
        url: `/livestream/v1/live/${liveId}/mute/${userId}`,
        method: 'GET',
    });
};

export const mute = (liveId: string, userId: string) => {
    return fetcher<null, { user_id: string }>({
        url: `/livestream/v1/live/${liveId}/mute`,
        method: "POST",
        data: { user_id: userId }
    })
}

export const unmute = (liveId: string, userId: string) => {
    return fetcher<null, { user_id: string }>({
        url: `/livestream/v1/live/${liveId}/unmute`,
        method: "POST",
        data: { user_id: userId }
    })
}
